import { useState, useEffect, forwardRef } from "react";
import { Box, Typography, Link, Avatar } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatchApp } from "../../lib/redux";
import { startGetProgressValues } from "../../actions/ui";
import {
  startCreateAdsCampaign,
  startSaveAdsCampaign,
  startSaveDraftCampaign,
  startGetRecommendedSettingsPlatform,
} from "../../actions/adsCampaigns";
import { off } from "firebase/database";
import { getPathDatabaseByRole } from "../../actions/auth";
import {
  BUTTON_PUBLISH_CAMPAIGN,
  BUTTON_SAVE_AND_PUBLISH,
  PUBLISHING_ALERT_SUCCESS_TITLE,
  PUBLISHING_ALERT_TITLE,
  PUBLISHING_ALERT_RETRY_BUTTON,
  PUBLISHING_ALERT_ERROR_TITLE,
  CREATE_ADS_CAMPAIGN_LOADING_GETTING_REQUIRED_INFORMATION,
  CREATE_ADS_CAMPAIGN_LOADING_ANALYZING_INFORMATION,
  CREATE_ADS_CAMPAIGN_LOADING_GENERATING_RECOMMENDATIONS,
  CREATE_ADS_CAMPAIGN_LOADING_FINALIZING_DETAILS,
  GENERATING_CAMPAIGN,
  CAMPAIGN_GENERATED_SUCCESS,
  CAMPAIGN_GENERATED_ERROR,
  CAMPAIGN_GENERATED_ERROR_DESCRIPTION,
  BUTTON_DISCARD,
  AD_GROUP,
  AD,
  ASSETS_GROUP,
  CAMPAIGN,
} from "../../i18n/keysTranslations";
import { errorCodeToText } from "../../utils/errors";
import linkifyHtml from "linkify-html";
import { useTranslationApp } from "../../lib/i18next";
import { ALERTS_ASYNC_STATUS } from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import { logoPlatform } from "../../utils/string";
import Button from "../Buttons/Button";
import { updateNotification } from "../../actions/alerts";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";

const getTaskVisualStatus = ({ isError, isSuccess }) => {
  if (isError) return "error";
  if (isSuccess) return "success";
  return "inProgress";
};

const CampaignAlertItem = forwardRef(({ alert, onClose }, ref) => {
  const [progress, setProgress] = useState(alert.progress || 0);
  const [reference, setReference] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();
  const currentPath = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const campaignIDFromURL = searchParams.get("campaignID");

  const {
    campaignID,
    isCreating,
    status,
    message,
    failedCampaigns,
    errorsCampaigns,
    currentCampaign,
    buttonAction,
    isGenerating,
    platform,
    platformGeneratingData,
    newPlatforms,
  } = alert;

  const isCampaignPage =
    currentPath === "/ads-campaign" && campaignID === campaignIDFromURL;

  const { userID } = dispatch(getPathDatabaseByRole());
  const isError = status === ALERTS_ASYNC_STATUS.ERROR;
  const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
  const isCreatingState = isCreating && status === ALERTS_ASYNC_STATUS.LOADING;
  const isGeneratingInProgress = isGenerating && !isError && !isSuccess;

  const {
    loaderTimeMs: generatingLoaderTimeMs,
    onProgressChange: onGeneratingProgressChange,
    seedProgress: generatingSeedProgress,
    formattedRemainingTime,
  } = useCreativeJobProgressLoader({
    storageKey: campaignID,
    jobType: CREATIVE_JOB_TYPES.generateRecommendedSettingsPlatform,
    isTerminal: !isGeneratingInProgress,
  });
  const additionalParams =
    isError && buttonAction === BUTTON_SAVE_AND_PUBLISH
      ? "&doSync=false&showDraft=true"
      : "";
  const isAddPlatform = buttonAction === "addPlatform";

  const campaignToShow = currentCampaign;

  // Optimized functions to get campaign data
  const getCampaignNameByPlatform = (platform) => {
    return campaignToShow?.platformsData?.[platform]?.name || "";
  };

  // Get translated error message for a platform
  const getTranslatedErrorMessage = (error, platformData) => {
    if (!error) return "";

    const code = error?.code;
    const pageID = platformData?.pageID;
    const businessID = platformData?.businessID;

    return errorCodeToText(code) === code
      ? error?.message
      : errorCodeToText(code, { pageID, businessID });
  };

  // Get text scope for error details
  const getTextScope = (error, campaign, platform) => {
    const additionalData = error?.additionalData || {};
    const scope = additionalData?.scope || "";

    if (scope === "campaign") {
      const name = getCampaignNameByPlatform(platform);
      return `<div><strong>${t(CAMPAIGN)}</strong>: ${name}</div>`;
    }

    const assetsGroupID = additionalData?.assetsGroupID;
    const adGroupID = additionalData?.adGroupID;
    const adID = additionalData?.adID;

    if (!adGroupID && !adID && !assetsGroupID) return "";

    // Helper function to get name or fallback to index number
    const getNameOrFallback = (name, index) => {
      if (name) return name;
      if (index !== undefined && index !== null && index >= 0) {
        return index + 1;
      }
      return "";
    };

    if (assetsGroupID) {
      const assetsGroupIndex = campaign?.assetsGroups?.findIndex(
        (assetsGroup) =>
          assetsGroup.id === assetsGroupID ||
          assetsGroup.idPlatform === assetsGroupID
      );
      if (assetsGroupIndex >= 0) {
        const assetsGroup = campaign.assetsGroups[assetsGroupIndex];
        const assetsGroupName = getNameOrFallback(
          assetsGroup.name,
          assetsGroupIndex
        );
        return `<div><strong>${t(ASSETS_GROUP)}</strong>: ${assetsGroupName}</div>`;
      }
      return "";
    }

    if (adID && adGroupID) {
      const adGroupIndex = campaign?.adsGroups?.findIndex(
        (adGroup) =>
          adGroup.id === adGroupID || adGroup.idPlatform === adGroupID
      );
      if (adGroupIndex >= 0) {
        const adGroup = campaign.adsGroups[adGroupIndex];
        const adIndex = adGroup.ads?.findIndex(
          (ad) => ad.id === adID || ad.idPlatform === adID
        );
        if (adIndex >= 0) {
          const ad = adGroup.ads[adIndex];
          const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
          const adName = getNameOrFallback(ad.name, adIndex);
          return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div><div><strong>${t(AD)}</strong>: ${adName}</div>`;
        }
        const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
        return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div>`;
      }
      return "";
    }

    if (adGroupID) {
      const adGroupIndex = campaign?.adsGroups?.findIndex(
        (adGroup) =>
          adGroup.id === adGroupID || adGroup.idPlatform === adGroupID
      );
      if (adGroupIndex >= 0) {
        const adGroup = campaign.adsGroups[adGroupIndex];
        const adGroupName = getNameOrFallback(adGroup.name, adGroupIndex);
        return `<div><strong>${t(AD_GROUP)}</strong>: ${adGroupName}</div>`;
      }
      return "";
    }

    return "";
  };

  // Build path with scope parameters from additionalData
  const buildPathWithScope = (
    campaignID,
    platform,
    additionalData,
    additionalParams
  ) => {
    let path = `/ads-campaign?campaignID=${campaignID}`;

    // Add platform if available
    const errorPlatform = additionalData?.platform || platform;
    if (errorPlatform) {
      path += `&platform=${errorPlatform}`;
    }

    // Add scope parameters based on additionalData
    if (additionalData) {
      const { adGroupID, adID, assetsGroupID } = additionalData;

      // For Google: add assetsGroupID if present
      if (assetsGroupID) {
        path += `&assetsGroupID=${assetsGroupID}`;
      }

      // For Meta/other platforms: add adGroupID and adID if present
      if (adGroupID) {
        path += `&adGroupID=${adGroupID}`;
      }
      if (adID) {
        path += `&adID=${adID}`;
      }
    }

    // Add additional params (doSync, showDraft, etc.)
    if (additionalParams) {
      path += additionalParams;
    }

    return path;
  };

  // Get campaign errors for a specific platform
  const getCampaignErrorsByPlatform = (platform) => {
    if (!errorsCampaigns?.[platform]) return null;

    const error = errorsCampaigns[platform];
    const platformData = campaignToShow?.platformsData?.[platform];

    return {
      code: error?.code,
      message: getTranslatedErrorMessage(error, platformData),
      scope: getTextScope(error, campaignToShow, platform),
      additionalData: error?.additionalData,
    };
  };

  // Handle navigation to campaign details
  const onCampaignClick = (path) => {
    if (campaignID) {
      navigate(path);
    }
  };

  // Handle alert click to navigate to campaign
  const onAlertClick = (event) => {
    // Prevent navigation if clicking on retry button or close button
    if (event.target.closest("button")) {
      return;
    }

    // Only navigate if there's a campaignID
    if (campaignID) {
      let path = `/ads-campaign?campaignID=${campaignID}${additionalParams}`;

      // If there are errors, redirect to the first platform with error and include scope parameters
      if (isError && errorsCampaigns) {
        // Get the first platform with error (from failedCampaigns or errorsCampaigns keys)
        const firstErrorPlatform =
          failedCampaigns && failedCampaigns.length > 0
            ? failedCampaigns[0]
            : Object.keys(errorsCampaigns)[0];

        if (firstErrorPlatform && errorsCampaigns[firstErrorPlatform]) {
          const error = errorsCampaigns[firstErrorPlatform];
          const additionalData = error?.additionalData || {};
          path = buildPathWithScope(
            campaignID,
            firstErrorPlatform,
            additionalData,
            additionalParams
          );
        }
      }

      onCampaignClick(path);
      // If the alert is in SUCCESS, delete it
      if (isSuccess) {
        onClose(campaignID);
      }
    }
  };

  // Common function to cleanup and reset state
  const cleanupAndReset = () => {
    if (reference) {
      off(reference);
      setReference(null);
    }
    setProgress(0);
  };

  // Common function to update alert status
  const updateAlertStatus = (updates) => {
    dispatch(updateNotification(campaignID, updates));
  };

  // Common function to handle retry response
  const onRetryResponse = async (response, buttonAction) => {
    if (response?.ok) {
      updateAlertStatus({
        status: ALERTS_ASYNC_STATUS.SUCCESS,
        message: t(PUBLISHING_ALERT_SUCCESS_TITLE),
        progress: 100,
        isCreating: false,
        failedCampaigns: response?.failedCampaigns,
      });
    } else {
      if (buttonAction === BUTTON_SAVE_AND_PUBLISH) {
        await dispatch(
          startSaveDraftCampaign({
            campaignID,
            campaign: response?.campaign,
          })
        );
      }
      updateAlertStatus({
        currentCampaign: response?.campaign,
        status: ALERTS_ASYNC_STATUS.ERROR,
        message: t(PUBLISHING_ALERT_ERROR_TITLE),
        progress: 100,
        isCreating: false,
        failedCampaigns: response?.failedCampaigns,
        errorsCampaigns: response?.errors,
      });
    }
  };

  // Retry publishing campaign
  const retryPublishCampaign = async () => {
    if (!currentCampaign || !campaignID) return;

    cleanupAndReset();

    updateAlertStatus({
      isCreating: true,
      status: ALERTS_ASYNC_STATUS.LOADING,
      message: t(PUBLISHING_ALERT_TITLE),
      progress: 0,
      buttonAction: BUTTON_PUBLISH_CAMPAIGN,
    });

    const response = await dispatch(
      startCreateAdsCampaign({ ...currentCampaign, campaignID })
    );
    onRetryResponse(response, BUTTON_PUBLISH_CAMPAIGN);
  };

  // Retry save and publish campaign
  const retrySaveAndPublishCampaign = async () => {
    if (!currentCampaign || !campaignID) return;

    cleanupAndReset();

    updateAlertStatus({
      isCreating: true,
      status: ALERTS_ASYNC_STATUS.LOADING,
      message: t(PUBLISHING_ALERT_TITLE),
      progress: 0,
      buttonAction: BUTTON_SAVE_AND_PUBLISH,
    });

    const response = await dispatch(
      startSaveAdsCampaign({ campaign: currentCampaign, campaignID })
    );
    onRetryResponse(response, BUTTON_SAVE_AND_PUBLISH);
  };

  const onRetryGenerating = async () => {
    updateAlertStatus({
      isCreating: false,
      isGenerating: true,
      status: ALERTS_ASYNC_STATUS.GENERATING,
      message: t(GENERATING_CAMPAIGN),
      progress: 0,
      buttonAction: "addPlatform",
    });

    const response = await dispatch(
      startGetRecommendedSettingsPlatform({
        ...platformGeneratingData,
        ...currentCampaign,
        platforms: newPlatforms,
        campaignID,
      })
    );

    if (response.ok) {
      dispatch(
        updateNotification(campaignID, {
          status: ALERTS_ASYNC_STATUS.SUCCESS,
          message: t(CAMPAIGN_GENERATED_SUCCESS),
          isCreating: false,
          isGenerating: false,
        })
      );
    } else {
      console.log("error en onRetryGenerating");
      dispatch(
        updateNotification(campaignID, {
          status: ALERTS_ASYNC_STATUS.ERROR,
          message: t(CAMPAIGN_GENERATED_ERROR),
          isCreating: false,
          isGenerating: false,
          errorsCampaigns: {
            [platformGeneratingData.platform]: {
              message: t(CAMPAIGN_GENERATED_ERROR_DESCRIPTION),
            },
          },
        })
      );
    }
  };

  // Handle retry button click
  const onRetry = async () => {
    const isPublishAction = buttonAction === BUTTON_PUBLISH_CAMPAIGN;
    if (isCampaignPage) {
      const publishBtn = document.getElementById("publish-campaign-button");
      const saveAndPublishBtn = document.getElementById(
        "save-and-publish-button"
      );
      if (publishBtn && isPublishAction) {
        publishBtn.click();
        return;
      } else if (saveAndPublishBtn && !isPublishAction) {
        saveAndPublishBtn.click();
        return;
      }
      return;
    }

    await (isPublishAction
      ? retryPublishCampaign()
      : retrySaveAndPublishCampaign());
  };

  // Update progress based on received data
  const onUpdateProgress = (data = {}) => {
    const { step, totalSteps } = data;

    if (!step || !totalSteps) return;

    let newProgress = Math.min((step / totalSteps) * 100, 100);
    // Allow progress to update even if it's less than current progress
    // This is necessary for retry scenarios where the process starts over
    setProgress(newProgress);
  };

  // Render campaigns list with links and errors
  const renderCampaignsList = (campaigns, isErrorList = false) => {
    if (!campaignToShow?.platformsData) return null;

    const platformsToShow = isErrorList
      ? campaigns
      : Object.keys(campaignToShow.platformsData);

    return (
      <Box>
        {platformsToShow.map((platform, idx) => {
          const campaignErrors = getCampaignErrorsByPlatform(platform);
          return (
            <Box
              key={platform}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 1,
                mt: idx > 0 ? 0.5 : 0,
              }}
            >
              <Link
                variant="body2"
                onClick={() => {
                  if (campaignID) {
                    const error = errorsCampaigns?.[platform];
                    const additionalData = error?.additionalData || {};
                    const path = buildPathWithScope(
                      campaignID,
                      platform,
                      additionalData,
                      additionalParams
                    );
                    onCampaignClick(path);
                  }
                }}
                sx={{
                  textDecoration: campaignID ? "underline" : "none",
                  cursor: campaignID ? "pointer" : "default",
                  display: "block",
                  fontWeight: 400,
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  color: "text.primary",
                }}
              >
                {`${platform.toUpperCase()} - ${getCampaignNameByPlatform(platform)}`}
              </Link>
              {isError && campaignErrors && (
                <TaskAlertErrorMessage>
                  {campaignErrors.scope && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.primary",
                        width: "100%",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: campaignErrors.scope,
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: "100%",
                      color: "text.primary",
                      flex: 1,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      whiteSpace: "pre-line",
                      "& *": {
                        p: "revert",
                        m: "revert",
                        listStyle: "revert",
                        textDecoration: "revert",
                      },
                      "& a": {
                        textDecoration: "underline",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: linkifyHtml(
                        (campaignErrors.message || "")?.replace(
                          /\n/g,
                          "<br />"
                        ),
                        {
                          rel: "noopener noreferrer",
                          target: "_blank",
                          style: "color: #00A3FF",
                        }
                      ),
                    }}
                  />
                </TaskAlertErrorMessage>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Function to render the list of campaigns according to the state
  const renderConditionalCampaignsList = () => {
    // Case 1: There is error and not addPlatform, show only failed campaigns
    if (
      isError &&
      !isAddPlatform &&
      failedCampaigns &&
      failedCampaigns.length > 0
    ) {
      return renderCampaignsList(failedCampaigns, true);
    }

    // Case 2: No error and not addPlatform, show all campaigns
    if (!isError && !isAddPlatform) {
      return renderCampaignsList();
    }

    // In any other case, do not show anything
    return null;
  };

  // Render action buttons (cancel and retry)
  // Campaign alerts only dismiss a status chip — no irreversible generated asset,
  // so Discard does not confirm (unlike creative/video/animation success).
  const renderButtons = () => {
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {(isError || isSuccess) && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onClose(campaignID);
            }}
            size="small"
            color="secondary"
            sx={{
              height: 32,
              fontSize: "0.75rem",
              minWidth: 100,
            }}
          >
            {t(BUTTON_DISCARD)}
          </Button>
        )}
        {!isAddPlatform && isError && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
            color="primary"
            size="small"
            sx={{
              height: 32,
              fontSize: "0.75rem",
              minWidth: 100,
            }}
          >
            {t(PUBLISHING_ALERT_RETRY_BUTTON)}
          </Button>
        )}
        {!isGenerating && isAddPlatform && isError && (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onRetryGenerating();
            }}
            size="small"
            color="primary"
            sx={{
              height: 32,
              fontSize: "0.75rem",
              minWidth: 100,
            }}
          >
            {t(PUBLISHING_ALERT_RETRY_BUTTON)}
          </Button>
        )}
      </Box>
    );
  };

  // Renders the main message with animated icon if applicable
  const renderMainMessage = () => (
    <>
      {isGenerating && platform && (
        <Avatar
          src={logoPlatform(platform)}
          variant="square"
          sx={{
            width: 30,
            height: 30,
            mb: 0.2,
            ".MuiAvatar-img": { objectFit: "contain" },
          }}
        />
      )}{" "}
      {message}
    </>
  );

  // Effect to handle progress tracking and cleanup
  useEffect(() => {
    let timeout1, timeout2;

    // Clean previous listener if exists
    if (reference) {
      off(reference);
      setReference(null);
    }

    if (isCreatingState && campaignID && userID) {
      setProgress(0);

      const progressID = `processProgress/${userID}/${campaignID}/`;

      dispatch(
        startGetProgressValues({
          progressID,
          onReferenceAvailable: (ref) => setReference(ref),
          onCallbackUpdateProgress: onUpdateProgress,
        })
      );

      timeout1 = setTimeout(() => {
        setProgress((prevProgress) =>
          prevProgress < 5 ? prevProgress + 5 : prevProgress
        );
      }, 2000);

      timeout2 = setTimeout(() => {
        setProgress((prevProgress) =>
          prevProgress < 10 ? prevProgress + 5 : prevProgress
        );
      }, 4000);
    } else if ((isSuccess || isError) && (campaignID || isSuccess)) {
      setProgress(100);
    } else {
      setProgress(0);
    }

    return () => {
      if (timeout1) clearTimeout(timeout1);
      if (timeout2) clearTimeout(timeout2);
      if (reference) {
        off(reference);
      }
    };
    // eslint-disable-next-line
  }, [
    isCreatingState,
    isSuccess,
    isError,
    campaignID,
    userID,
    dispatch,
    message,
  ]);

  const isInProgress = !isError && !isSuccess;
  const taskVisualStatus = getTaskVisualStatus({ isError, isSuccess });

  return (
    <TaskAlertCard
      ref={ref}
      status={taskVisualStatus}
      label={renderMainMessage()}
      estimatedTime={
        isGeneratingInProgress ? formattedRemainingTime : null
      }
      progress={progress}
      onClick={onAlertClick}
      cursor={campaignID ? "pointer" : "default"}
      showProgressBar={isInProgress && !alert.isGenerating}
      showProgressPercent={isInProgress && !alert.isGenerating}
      progressSlot={
        isInProgress && alert.isGenerating ? (
          <LinearLoaderWithTime
            isAlert={true}
            labelSide="bottom"
            time={generatingLoaderTimeMs}
            progress={generatingSeedProgress}
            autoIncrement={true}
            max={99}
            onProgressChange={onGeneratingProgressChange}
            labels={[
              t(CREATE_ADS_CAMPAIGN_LOADING_GETTING_REQUIRED_INFORMATION),
              t(CREATE_ADS_CAMPAIGN_LOADING_ANALYZING_INFORMATION),
              t(CREATE_ADS_CAMPAIGN_LOADING_GENERATING_RECOMMENDATIONS),
              t(CREATE_ADS_CAMPAIGN_LOADING_FINALIZING_DETAILS),
            ]}
            sxLabel={{ fontSize: "0.75rem", color: "text.secondary" }}
          />
        ) : null
      }
      details={renderConditionalCampaignsList()}
      actions={isError || isSuccess ? renderButtons() : null}
    />
  );
});

CampaignAlertItem.displayName = "CampaignAlertItem";

export default CampaignAlertItem;
